import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, fork, put, select, takeEvery, take, delay } from 'redux-saga/effects';
import { RootState } from 'types';
import { EntityTypes } from 'constants/entities';
import { ActionEntityType, ActionStatus, IActionDAO, IActionEntityDAO } from 'types/domain/action';
import { selectActionDAO } from 'core/selectors/product/action/action';
import { selectActionEntity } from 'core/selectors/product/action/actionEntity';
import { updateState } from 'core/actions/product/state';
import { IModuleDAO, IModuleOut } from 'types/domain/module';
import {
  actionSubmit,
  IActionRegister,
  IRegisterAction,
  ISubmitAction,
  next,
  registerAction,
  registerActionEntity,
  registerActionResult,
  RegisterResult,
  resetAction,
} from 'core/slices/register';
import { selectActionRegister } from 'core/selectors/product/action/actionRegister';
import { Handles } from 'constants/handles';
import { updatePlayerState } from 'core/slices/player';
import { IActionFeed, selectActionFeed } from 'core/selectors/product/action/actionFeed';
import { VariableType } from 'types/domain/variable';
import { selectSessionMode } from 'core/selectors/admin/sessionMode';
import { IPreviewVariable, updatePreview } from 'core/slices/preview';
import { Mode } from 'core/slices/session';
import { selectModuleDAO } from 'core/selectors/product/module';

function* handleRegister(action: PayloadAction<Partial<IRegisterAction>>) {
  //@ts-ignore
  const actionDAO: IActionDAO = yield select(s => selectActionDAO(s, action.payload.actionId));
  const actionFeed: IActionFeed = yield select(selectActionFeed);
  const isFinal = actionFeed.index === actionFeed.feed.length - 1;
  const feedbackDelay = 700;

  if (action.payload.actionEntityId) {
    const modOut = actionDAO.outs[action.payload.actionEntityId];
    if (modOut) {
      yield delay(200);
      return yield moduleOut(modOut);
    }
  }

  if (action.payload.result !== null && actionDAO) {
    switch (action.payload.result) {
      case RegisterResult.PASS:
        yield delay(feedbackDelay);
        if (actionDAO.outs[Handles.PASS]) {
          return yield moduleOut(actionDAO.outs[Handles.PASS], true);
        } else {
          yield put(yield call(next, { auto: isFinal, interaction: true, culprit: 'handleRegister.pass' }));
        }
        break;
      case RegisterResult.INFO:
      case RegisterResult.DEFAULT:
        yield delay(200);
        yield put(yield call(next, { auto: isFinal, interaction: true, culprit: 'handleRegister.default' }));
        break;
      case RegisterResult.FAIL:
        yield delay(feedbackDelay);
        if (actionDAO.outs[Handles.FAIL]) {
          return yield moduleOut(actionDAO.outs[Handles.FAIL], true);
        } else {
          yield put(yield call(next, { auto: isFinal, interaction: true, culprit: 'handleRegister.fail' }));
        }
        break;
      case RegisterResult.TIME:
        if (actionDAO.outs[Handles.TIME]) return yield moduleOut(actionDAO.outs[Handles.TIME]);
        else yield put(yield call(next, { auto: isFinal, interaction: true, culprit: 'handleRegister.time' }));
        break;
    }
  }
}

function* handleSubmit(action: PayloadAction<ISubmitAction>) {
  //@ts-ignore
  const actionDAO: IActionDAO = yield select(s => selectActionDAO(s, action.payload.actionId));
  const actionRegister: IActionRegister = yield select(state => selectActionRegister(state, action.payload.actionId));

  // Defensive: if the action/register vanished between submit dispatch and now,
  // don't throw (which would kill this worker and leave the player stuck) —
  // fall back to advancing so navigation can't wedge.
  if (!actionDAO || !actionRegister) {
    console.warn('[nav] handleSubmit: missing actionDAO/register, advancing directly', action.payload);
    yield put(yield call(next, { culprit: 'handleSubmit.fallback' }));
    return;
  }

  const failed = actionDAO.entities.some(
    ent =>
      (ent.correct && !actionRegister.entities[ent.id]?.selected) ||
      (!ent.correct && actionRegister.entities[ent.id]?.selected),
  );

  yield put(
    yield call(registerActionResult, {
      actionId: actionDAO.id,
      courseProgressId: actionRegister.courseProgressId,
      sessionAttemptId: actionRegister.sessionAttemptId,
      result: actionDAO.isMultichoice ? (failed ? RegisterResult.FAIL : RegisterResult.PASS) : RegisterResult.DEFAULT,
    }),
  );
}

function* handleEntityRegister(action: PayloadAction<Partial<IRegisterAction>>) {
  //@ts-ignore
  const actionDAO: IActionDAO = yield select(s => selectActionDAO(s, action.payload.actionId));

  if (!actionDAO || !action.payload.actionEntityId)
    return console.warn('action entity register invalid', action.payload);

  const actionEntity: IActionEntityDAO = yield select((state: RootState) =>
    selectActionEntity(state, action.payload.actionEntityId),
  );

  if (actionEntity.type === ActionEntityType.TEXT) return;

  let actionRegister: IActionRegister = yield select(state => selectActionRegister(state, action.payload.actionId));

  while (!actionRegister) {
    yield take(registerAction);
    actionRegister = yield select(state => selectActionRegister(state, action.payload.actionId));
  }

  //yield put(yield call(updateState, { type: EntityTypes.ACTION_ENTITY, id: actionEntity.id }));
  yield put(yield call(updatePlayerState, { paused: true, cuplrit: 'HandleEntityRegister' }));

  // if there is 1 right answer, and no branching, then this action is complete
  yield updatePreviewVariables(actionDAO, actionEntity.value, actionEntity.variableOptionId);

  const correctAnswers = actionDAO.entities.filter(e => e.correct && e.status > -1);

  if (correctAnswers.length < 2)
    yield put(
      yield call(registerActionResult, {
        actionId: actionDAO.id,
        courseProgressId: actionRegister.courseProgressId,
        actionEntityId: action.payload.actionEntityId,
        sessionAttemptId: action.payload.sessionAttemptId,
        result:
          correctAnswers.length > 0
            ? actionEntity.correct
              ? RegisterResult.PASS
              : RegisterResult.FAIL
            : RegisterResult.DEFAULT,
      }),
    );
  else if (actionDAO.outs[action.payload.actionEntityId])
    yield moduleOut(actionDAO.outs[action.payload.actionEntityId]);
}

export function* moduleOut(moduleOut: IModuleOut, isChild = false) {
  if (!moduleOut) return; //yield put(yield call(next));
  //@ts-ignore
  const action: IActionDAO = yield select((state: RootState) => selectActionDAO(state, moduleOut.targetId));

  if (!action || action.status === ActionStatus.DELETED) yield put(yield call(next, { culprit: 'broken moduleOut' }));
  else yield nextAction(moduleOut.targetId, isChild);
}

function* updatePreviewVariables(action: IActionDAO, value, variableOptionId) {
  const mode: Mode = yield select(selectSessionMode);
  if (mode === Mode.PREVIEW && (!!value || !!variableOptionId)) {
    const actionRegister: IActionRegister = yield select(state => selectActionRegister(state, action.id));
    const module: IModuleDAO = yield select(state => selectModuleDAO(state, action.moduleId));
    const multiplyer = () => {
      if (!action.timeLimit || !action.timeScore) return null;
      const time = (actionRegister.completed - actionRegister.started) / 1000;
      const offset = (action.timeLimit - time) / action.timeLimit;
      const min = action.timeScore / 100;

      return min + offset * (1 - min);
    };
    const variable = action.variable || module.variable;
    if (!variable) return;
    const variableRegister: IPreviewVariable = yield select((state: RootState) => state.preview.variables[variable.id]);
    const data = () => {
      switch (variable.type) {
        case VariableType.BOOLEAN:
          return { pass: value === 1 };
        case VariableType.SCORE:
          const score = (variableRegister?.value ?? 0) + value;
          return { score: multiplyer() ? multiplyer() * score : score };
        case VariableType.OPTIONS:
          return { variableOptionId };
      }
    };

    yield put(
      yield call(updatePreview, {
        type: EntityTypes.VARIABLE,
        id: variable.id,
        data: data(),
      }),
    );
  }
}

export function* nextAction(id, isChild = false) {
  const actionId = yield select((state: RootState) => state.state[EntityTypes.ACTION]);
  const courseProgressId = yield select((state: RootState) => state.state[EntityTypes.COURSE_PROGRESS]);
  const sessionAttemptId = yield select((state: RootState) => state.state[EntityTypes.SESSION_ATTEMPT]);

  if (actionId !== id) {
    const feed: IActionFeed = yield select(selectActionFeed);
    const index = feed.feed.findIndex(fi => fi.actionId === id);

    if (index > -1)
      return yield put(
        yield call(resetAction, {
          index,
          actionId: id,
          courseProgressId,
          sessionAttemptId,
        }),
      );

    yield put(yield call(updateState, { type: EntityTypes.ACTION, id, culprit: 1 }));
    const action: PayloadAction<IActionRegister> = yield take(registerAction);

    if (action.payload.actionId !== id) return;
  }

  yield put(yield call(next, { culprit: 'nextAction', isChild }));
}

function* watchActionEntity() {
  yield takeEvery(registerActionEntity.type, handleEntityRegister);
}

function* watchSubmit() {
  yield takeEvery(actionSubmit, handleSubmit);
}

function* watchAction() {
  yield takeEvery(registerActionResult.type, handleRegister);
}

export function* actionSaga() {
  yield all([fork(watchAction), fork(watchActionEntity), fork(watchSubmit)]);
}
